import React from 'react';
import Progress from '../Progress';
import { compactCount, compactOrDash } from './tokenText';

export interface ChatContextMeterProps {
    /** Tokens spent. Absent reads as a dash: nothing has been measured yet. */
    used?: number | null;
    /** The model's context window. */
    limit: number;
    /** Tokens served from cache, when the provider reports them. */
    cached?: number | null;
    label?: string;
    className?: string;
}

// The same surface and shape `Chat.ComposerChip` gets from PillButton's `tab`, so the row
// under the field reads as one strip. Not PillButton itself: this one holds a bar and three
// pieces of text, where a pill holds a label.
const CHIP_CLASSES =
    'inline-flex h-[var(--malachite-control-height-small,28px)] shrink-0 items-center gap-2 rounded-b-[var(--malachite-radius-10,10px)] rounded-t-none bg-[var(--malachite-pillbutton-background)] px-2.5 text-[length:var(--malachite-font-size-small,13px)] text-[var(--malachite-pillbutton-foreground)] whitespace-nowrap';

/**
 * How much of a model's context a conversation has spent.
 *
 * Its own chip rather than something to wrap, since it sits in the same row as the composer's
 * other chips and has to line up with them.
 */
const ChatContextMeter: React.FC<ChatContextMeterProps> = ({
    used,
    limit,
    cached,
    label = 'Context',
    className = '',
}) => {
    const spent = used ?? 0;
    // Clamped: a provider that reports more than the window would otherwise overflow the bar.
    const percent = limit > 0 ? Math.min(100, Math.max(0, (spent / limit) * 100)) : 0;

    return (
        <span className={`${CHIP_CLASSES} ${className}`}>
            <span>{label}</span>
            {/* `flexShrink: 0`, or a narrow row crushes the track to a few pixels: a flex
                item shrinks below its own width by default, and this one carries the reading. */}
            <span style={{ width: 96, flexShrink: 0 }}>
                <Progress
                    value={percent}
                    height={6}
                    aria-label={`${label}: ${compactOrDash(used)} of ${compactCount(limit)} tokens`}
                />
            </span>
            <span>{`${compactOrDash(used)}/${compactCount(limit)}`}</span>
            {cached === undefined ? null : <span>{`· Cached: ${compactOrDash(cached)}`}</span>}
        </span>
    );
};

export default ChatContextMeter;
export { ChatContextMeter };
